import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';

import * as Decorators from '@shared/core/decorators';
import * as Tokens from '@shared/core/tokens';

import * as Utils from '@shared/core/utils';

import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { ImagesMapper } from '@shared/core/mappers/images.shared.mapper';

@Injectable({
    providedIn: 'root',
})
export class ImagesService {
    constructor(
        @Inject(Tokens.CONFIG_TOKEN) protected _config: OLO.Config,
        protected _httpClient: HttpClient,
        protected _store: Store<OLO.State>,
        protected _rendererFactory2: RendererFactory2,
    ) {}

    public preloadImageInMemory(imgUrl: string): Observable<boolean> {
        return Observable.create((observer) => {
            if (!imgUrl || imgUrl === 'null') {
                observer.next(false);
                observer.complete();

                return;
            }
            let renderer: Renderer2 = this._rendererFactory2.createRenderer(null, null);

            const holder = renderer.createElement('div');
            const imgElem = renderer.createElement('img');
            renderer.setAttribute(imgElem, 'src', imgUrl);
            renderer.appendChild(holder, imgElem);
            imgElem.onload = () => {
                observer.next(true);
                observer.complete();
            };

            imgElem.onerror = () => {
                observer.next(false);
                observer.complete();
            };

            return () => {
                setTimeout(() => {
                    renderer.destroy();
                    renderer = null;
                }, 1000);
            };
        });
    }

    public getImageForOrderType(params: APICommon.ImagesGetParams, ...orderTypes: number[]): Observable<OLO.DTO.ImageUrlModel[]> {
        const stringParams: string = Utils.HTTP.idsToStringParams('orderTypes', orderTypes);
        const sizeParams: string = `${params.width ? `&width=${params.width}` : ''}${params.height ? `&height=${params.height}` : ''}`;

        return this._httpClient
            .get<APIv3.ImageGetOrderTypeImages.Responses.$200>(`${this._config.api.base}/images/orderType?${stringParams}${sizeParams}`)
            .pipe(map((results: APIv3.ImageGetOrderTypeImages.Responses.$200) => ImagesMapper.mapGetImageForOrderType(results)));
    }

    public getImageForLocation(locationNo: number, width: Nullable<number> = null, height: Nullable<number> = null): Observable<string> {
        const params: HttpParams = new HttpParams({
            fromObject: { width, height } as any,
        });

        return this._httpClient
            .get<APIv3.ImageGetLocationImage.Responses.$200>(`${this._config.api.base}/images/location/${locationNo}`, { params })
            .pipe(map((results: APIv3.ImageGetLocationImage.Responses.$200) => ImagesMapper.mapGetImageForLocation(results)));
    }

    public getImagesForLocationsByType(params: APICommon.CommonImagesGetParams, ...locationIds: number[]): Observable<OLO.DTO.ImageUrlModel[]> {
        const stringParams: string = Utils.HTTP.idsToStringParams('locationIds', locationIds);
        const sizeParams: string = `${params.width ? `&width=${params.width}` : ''}${params.height ? `&height=${params.height}` : ''}`;

        return this._httpClient
            .get<APIv3.ImageGetLocationsImages.Responses.$200>(`${this._config.api.base}/images/location?${stringParams}&imageType=${params.imageType || 4}${sizeParams}`)
            .pipe(map((results: APIv3.ImageGetLocationsImages.Responses.$200) => ImagesMapper.mapGetImagesForLocationsByType(results)));
    }

    public getProductImages(params: APICommon.ImagesGetParams, ...productIds: number[]): Observable<OLO.DTO.ImageUrlModel[]> {
        const stringParams: string = Utils.HTTP.idsToStringParams('productIds', productIds);

        return this._httpClient
            .get<APIv3.ImageGetProductImages.Responses.$200>(`${this._config.api.base}/images/Products?${stringParams}&width=${params.width || 150}&height=${params.height || 150}`)
            .pipe(map((results: APIv3.ImageGetProductImages.Responses.$200) => ImagesMapper.mapGetProductImages(results)));
    }

    public getImagesForOnlineMenuPages(params: APICommon.ImagesGetParams, imageType: OLO.Enums.IMAGE_TYPE, pageIds: number[]): Observable<OLO.DTO.ImageUrlModel[]> {
        const stringParams: string = Utils.HTTP.idsToStringParams('pageId', pageIds);

        return this._httpClient
            .get<APIv3.ImageGetOnlineMenuPageImages.Responses.$200>(
                `${this._config.api.base}/images/onlineMenuPage${Utils.HTTP.object2string(params)}&${stringParams}&imageType=${imageType}`,
            )
            .pipe(map((results: APIv3.ImageGetOnlineMenuPageImages.Responses.$200) => ImagesMapper.mapGetImagesForOnlineMenuPages(results)));
    }

    public getImagesForOnlineMenuPageProducts(params: APICommon.ImagesGetParams, pageId: number): Observable<OLO.DTO.ImageUrlModel[]> {
        return this._httpClient
            .get<APIv3.ImageGetOnlineMenuPageProductsImages.Responses.$200>(`${this._config.api.base}/images/onlineMenuPageProducts/${pageId}`)
            .pipe(map((results: APIv3.ImageGetOnlineMenuPageProductsImages.Responses.$200) => ImagesMapper.mapGetImagesForOnlineMenuPageProducts(results)));
    }

    public getDietaryTagsImages(params: APICommon.ImagesGetParams, ...dietaryTagIds: number[]): Observable<OLO.DTO.ImageUrlModel[]> {
        const stringParams: string = Utils.HTTP.idsToStringParams('dietaryTagId', dietaryTagIds);

        return this._httpClient
            .get<APIv3.ImageGetDietaryTagsImages.Responses.$200>(
                `${this._config.api.base}/images/dietaryTags?${stringParams}&width=${params.width || 150}&height=${params.height || 150}`,
            )
            .pipe(map((results: APIv3.ImageGetDietaryTagsImages.Responses.$200) => ImagesMapper.mapGetDietaryTagsImages(results)));
    }

    public getInteracts(params: APICommon.InteractImagesGetParams, ...productIds: number[]): Observable<OLO.DTO.InteractImageUrlModel[]> {
        const stringParams: string = Utils.HTTP.idsToStringParams('productId', productIds);

        return this._httpClient
            .get<APIv3.ImageGetInteractImages.Responses.$200>(`${this._config.api.base}/images/interact${Utils.HTTP.object2string(params)}&${stringParams}`)
            .pipe(map((results: APIv3.ImageGetInteractImages.Responses.$200) => ImagesMapper.mapGetInteracts(results)));
    }

    public getMenuFlowImages(params: APICommon.ImagesGetParams, ...menuFlowIds: number[]): Observable<OLO.DTO.ImageUrlModel[]> {
        const stringParams: string = Utils.HTTP.idsToStringParams('menuFlowId', menuFlowIds);

        return this._httpClient
            .get<APIv3.ImageGetMenuFlowsImages.Responses.$200>(
                `${this._config.api.base}/images/menuFlows?${stringParams}&width=${params.width || 150}&height=${params.height || 150}`,
            )
            .pipe(map((results: APIv3.ImageGetMenuFlowsImages.Responses.$200) => ImagesMapper.mapGetMenuFlowImages(results)));
    }

    public getVenueImagesByType(params: APICommon.CommonImagesGetParams, ...venueIds: number[]): Observable<OLO.DTO.ImageUrlModel[]> {
        const stringParams: string = Utils.HTTP.idsToStringParams('venueIds', venueIds);

        return this._httpClient
            .get<APIv3.ImageGetVenuesImages.Responses.$200>(
                `${this._config.api.base}/images/venue?${stringParams}&imageType=${params.imageType || 4}&width=${params.width || 150}&height=${params.height || 150}`,
            )
            .pipe(map((results: APIv3.ImageGetVenuesImages.Responses.$200) => ImagesMapper.mapVenueImagesByType(results)));
    }

    public getImageForLoyaltyIntroductionPage(pageId: number, params: APICommon.ImagesGetParams = {}): Observable<string> {
        return this._httpClient
            .get<APIv3.ImageGetIntroductionPageImages.Responses.$200>(
                `${this._config.api.base}/images/introductionPage/${pageId}?width=${params.width || 300}&height=${params.height || 300}`,
            )
            .pipe(map((results: APIv3.ImageGetIntroductionPageImages.Responses.$200) => ImagesMapper.mapGetImageForLoyaltyIntroductionPage(results)));
    }

    public getImageForMemberFreeProduct(productId: number, params: APICommon.CommonImagesGetParams = {}): Observable<string> {
        return this._httpClient
            .get<APIv3.ImageGetMemberFreeProductImage.Responses.$200>(
                `${this._config.api.base}/images/memberFreeProducts/${productId}?width=${params.width || 300}&height=${params.height || 300}&imageType=${params.imageType || 4}`,
            )
            .pipe(map((results: APIv3.ImageGetMemberFreeProductImage.Responses.$200) => ImagesMapper.mapGetImageForMemberFreeProduct(results)));
    }

    public getMemberCardBarcode(
        memberId: number,
        params: APICommon.ImagesGetParams = {},
        type: OLO.Enums.BARCODE_SYMBOLOGY = OLO.Enums.BARCODE_SYMBOLOGY.CODE_QR,
    ): Observable<string> {
        return this._httpClient
            .get<APIv3.ImageGetMemberCardBarcodeUrl.Responses.$200>(
                `${this._config.api.base}/images/memberCardBarcodes/${memberId}?barcodeSymbology=${type}&width=${params.width || window.innerWidth}&height=${
                    params.height || window.innerWidth
                }`,
            )
            .pipe(map((results: APIv3.ImageGetMemberCardBarcodeUrl.Responses.$200) => ImagesMapper.mapGetMemberCardBarcode(results)));
    }
}
